import omit from 'lodash-es/omit'
import { z } from 'zod'
import { WebSocketSubject } from 'rxjs/webSocket'
import { Map } from 'immutable'

import { OrganizationId } from '@/lib/core/interfaces/organization.types'

export const AssetTrackingDataReceivedSchema = z
  .object({
    altitude: z.number().default(0),
    'bridge-timestamp': z.coerce.date().optional(),
    'calc-geosp-dwell-id': z.union([z.string(), z.number()]).optional(),
    'device-timestamp': z.coerce.date(),
    'fleet-name': z.string(),
    heading: z.number().optional().default(0),
    latitude: z.number(),
    longitude: z.number(),
    odometer: z.number().optional().default(0),
    'provider-timestamp': z.coerce.date(),
    speed: z.number().optional(),
    'vehicle-bridge-id': z.string(),
    'vehicle-meta-data': z.any().optional(),
  })
  .strip()

export const AssetTrackingDataSchema = AssetTrackingDataReceivedSchema.transform((data) => {
  const v = {
    ...omit(data, [
      'bridge-timestamp',
      'calc-geosp-dwell-id',
      'fleet-name',
      'device-timestamp',
      'provider-timestamp',
      'vehicle-bridge-id',
    ]),
    fleetName: data['fleet-name'],
    sensorDatetime: data['device-timestamp'],
    sourceDatetime: data['provider-timestamp'],
    sourceId: data['vehicle-bridge-id'],
    dwellId: data['calc-geosp-dwell-id'],
    latitude: data.latitude,
    get lat() {
      return this.latitude
    },
    set lat(value: number) {
      this.latitude = value
    },
    longitude: data.longitude,
    get lon() {
      return this.longitude
    },
    set lon(value: number) {
      this.longitude = value
    },
    heading: data.heading,
    speed: data.speed,
  }
  return v
})

/**
 * `isMoving` is not produced by the schema itself - devices without speed data (e.g. sticker
 * trackers) never report it, so it's derived in the telemetry store from `dwellId`
 * (absence of a dwell id means the device hasn't settled into a stationary period) and
 * attached before an update is written to the tracking map.
 */
export type AssetTrackingData = z.infer<typeof AssetTrackingDataSchema> & {
  isMoving: boolean
}
export type MyTelemetryId = OrganizationId
export type MyTelemetryCredentials = {
  id: MyTelemetryId
  telemetryFleetName: string
  telemetryToken: string
}

export const socketStatus = z.enum([
  'DISCONNECTED',
  'CONNECTED',
  'AUTH_FAILED',
  'NOT_FOUND',
  'HANDSHAKE',
  'TIMEOUT',
  'TRACKING',
  'CLOSED',
])

export type SocketStatus = z.infer<typeof socketStatus>

export enum TelemetryStatus {
  UNINITIALIZED,
  CONNECTING,
  IDLE,
  LISTENING,
  ERROR,
}

export const statusMapping = {
  'not-found': socketStatus.enum.NOT_FOUND,
  hello: socketStatus.enum.HANDSHAKE,
  timeout: socketStatus.enum.TIMEOUT,
  success: socketStatus.enum.TRACKING,
  'invalid-token': socketStatus.enum.AUTH_FAILED,
} as const

export const statusResolver: Record<SocketStatus, TelemetryStatus> = {
  DISCONNECTED: TelemetryStatus.UNINITIALIZED,
  CONNECTED: TelemetryStatus.IDLE,
  AUTH_FAILED: TelemetryStatus.ERROR,
  NOT_FOUND: TelemetryStatus.ERROR,
  HANDSHAKE: TelemetryStatus.IDLE,
  TIMEOUT: TelemetryStatus.ERROR,
  TRACKING: TelemetryStatus.LISTENING,
  CLOSED: TelemetryStatus.UNINITIALIZED,
}

export type WebSocketMessage = AssetTrackingData | SocketStatus | string
export type WebSocketType = WebSocketSubject<WebSocketMessage>

export type TelemetryStore = {
  status: TelemetryStatus
  tracking: Map<string, AssetTrackingData>
  socketStates: Record<string, SocketStatus>
  ensureSocket(fleet: string, accessToken: string): WebSocketType
  startListeningToSocket(fleet: string, accessToken?: string): () => void
  socket(fleet: string | undefined): WebSocketType | undefined
  closeSocket(fleet: string): void

  activeListeners: number
  incrementActiveListeners(): void
  decrementActiveListeners(): void
}

// Keep mutable WebSocket subjects outside of Immer-managed state
export const sockets: Record<string, WebSocketType> = {}
